import React, { useEffect, useState } from "react"

import { useFormContext } from "react-hook-form"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import type { IIndexable } from "@common-shared/types/general.types.ts"
import { IPayer } from "@common-shared/types/responseData/commonResponseData.types"

import { FRONT_LOOKUP_CONFIG } from "@front-shared/config/lookup.config"
import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { CheckboxElement } from "@front-shared/elements/checkbox.element.tsx"
import { InputElement } from "@front-shared/elements/input.element.tsx"
import { InputErrorElement } from "@front-shared/elements/inputError.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { TooltipElement } from "@front-shared/elements/tooltip.element.tsx"
import { LookupService } from "@front-shared/services/lookup.service.ts"

import { CHECKER_CONFIG } from "../../../../config/checker.config"
import { useIsFeatureFlagEnabled } from "../../../../hooks/isFeatureFlagEnabled.hook"
import type { IDynamicCheckerFormInputData } from "../../../../types/data.types.ts"

export const MemberIdInput: React.FunctionComponent<
    IDynamicFormMemberIdInputProps
> = props => {
    const {
        formState: { errors },
        control,
        setValue,
        watch,
        register,
        unregister,
    } = useFormContext<IDynamicCheckerFormInputData>()

    const dataWatcher = watch()

    const memberIdNotAvailableEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
    )

    const [predictedPayer, setPredictedPayer] = useState<IPayer | null>(null)

    // Prediction silenced for the rest of the session (dismissed or overridden).
    const [isPayerPredictionSuppressed, setIsPayerPredictionSuppressed] =
        useState<boolean>(false)

    const isPayerPredictionEnabled = !!props.onApplyPayerPrediction

    // Only payers with a known brand icon show one inline - others show none.
    const payerIcon = predictedPayer
        ? CHECKER_CONFIG.payerIconByExternalId[predictedPayer.payerId]
        : undefined

    // The icon reflects identification - it stays while a payer is detected.
    // The hint is the call to action: it hides once the selected payer already
    // matches the prediction (whether applied or chosen manually) or when
    // suppressed - derived from live form state, never from an applied flag.
    const showPayerPredictionIcon = !!predictedPayer && !!payerIcon
    const showPayerPredictionHint =
        isPayerPredictionEnabled &&
        !!predictedPayer &&
        !isPayerPredictionSuppressed &&
        dataWatcher.payer?.payerId !== predictedPayer.payerId

    // Prediction only depends on the leading characters, so we key the fetch off
    // this prefix - typing past it won't re-hit the backend with the same lookup.
    const memberIdPredictionPrefix = (dataWatcher.memberId?.trim() ?? "").slice(
        0,
        FRONT_LOOKUP_CONFIG.memberIdPayerPredictionMinLength,
    )

    useEffect(() => {
        setTimeout(() => {
            setValue("memberId", props.formDefaultValue)
        })
    }, [])

    // Predict the payer from the member ID prefix once it reaches min length.
    useEffect(() => {
        // Clearing the member ID fully resets prediction state (incl. suppression)
        if (!isPayerPredictionEnabled || !memberIdPredictionPrefix) {
            resetPayerPredictionState()

            return
        }

        // Note: we keep identifying (and thus keep the icon accurate) even when
        // suppressed - suppression only hides the actionable hint, not the icon.

        if (
            memberIdPredictionPrefix.length <
            FRONT_LOOKUP_CONFIG.memberIdPayerPredictionMinLength
        ) {
            setPredictedPayer(null)

            return
        }

        let cancelled = false

        const timeoutId = setTimeout(async () => {
            try {
                const payer = await LookupService.predictPayerByMemberId(
                    memberIdPredictionPrefix,
                )

                if (cancelled) {
                    return
                }

                setPredictedPayer(payer)
            } catch (error) {
                console.error("Failed to predict payer:", error)
            }
        }, 300)

        return () => {
            cancelled = true
            clearTimeout(timeoutId)
        }
    }, [isPayerPredictionEnabled, memberIdPredictionPrefix])

    const resetPayerPredictionState = () => {
        setPredictedPayer(null)
        setIsPayerPredictionSuppressed(false)
    }

    // Hide the hint for the session, but keep predictedPayer so the
    // identified payer icon stays visible inline in the input.
    const suppressPayerPrediction = () => {
        setIsPayerPredictionSuppressed(true)
    }

    const onApplyPayerPrediction = () => {
        if (!predictedPayer) {
            return
        }

        // Applying just selects the payer - the hint then hides on its own once
        // the selected payer matches the prediction.
        props.onApplyPayerPrediction?.(predictedPayer)
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <InputElement
                label={
                    props.label ||
                    `Member ID${!props.required ? " (optional)" : ""}`
                }
                name="memberId"
                disabled={props.disabled}
                reactHookErrors={errors}
                reactHookControl={
                    !props.formControlDisabled ? (control as any) : undefined
                }
                shouldUnregister
                reactHookValidations={{
                    ...(props.required
                        ? {
                              required: FRONT_VALIDATION_CONFIG.required,
                          }
                        : {}),

                    ...(props.additionalValidations || {}),

                    maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                }}
                defaultValue={props.formDefaultValue}
                isLabelStatic={props.isLabelStatic}
                hideErrors={
                    props.hideErrors || props.displayErrorsBelowCheckbox
                }
                forceHighlightOnError={props.forceHighlightOnError}
                inputSuffix={
                    // Keep a stable suffix container while prediction is enabled
                    // so the icon appearing doesn't toggle AntD's affix wrapper
                    // (which recreates the input and drops focus mid-typing).
                    isPayerPredictionEnabled ? (
                        <span className="flex items-center justify-center w-[16px] h-[16px]">
                            {showPayerPredictionIcon ? (
                                <img
                                    src={payerIcon}
                                    alt={`${predictedPayer?.insuranceName} detected`}
                                    title={`${predictedPayer?.insuranceName} detected`}
                                    className="cursor-pointer w-[16px]"
                                    onClick={onApplyPayerPrediction}
                                />
                            ) : null}
                        </span>
                    ) : undefined
                }
            />

            {showPayerPredictionHint ? (
                <div className="flex items-center mt-[4px]">
                    <div
                        className="cursor-pointer"
                        onClick={onApplyPayerPrediction}
                    >
                        <PrimaryText
                            typography="captionSemibold"
                            color={NIRVANA_COLORS.brand.vibrantPurple}
                        >
                            {predictedPayer?.insuranceName} detected — click to
                            apply
                        </PrimaryText>
                    </div>

                    <div
                        className="cursor-pointer px-[4px] ml-[4px] leading-none"
                        onClick={suppressPayerPrediction}
                    >
                        <PrimaryText
                            typography="captionSemibold"
                            color={NIRVANA_COLORS.brand.vibrantPurple}
                        >
                            ×
                        </PrimaryText>
                    </div>
                </div>
            ) : null}

            {memberIdNotAvailableEligible && props.withNotAvailableCheckbox ? (
                <div className="flex items-center justify-start">
                    <CheckboxElement
                        className={`
                            mt-[4px]
                            ${dataWatcher.memberId ? "opacity-20" : ""}
                        `}
                        name="memberIdNotAvailable"
                        reactHookFormRegister={register as any}
                        reactHookFormUnregister={unregister as any}
                        reactHookFormSet={setValue as any}
                        watchDefaultCheckedChange
                        disabled={props.notAvailableDisabled}
                        defaultChecked={props.notAvailableDefaultChecked}
                        label={
                            <PrimaryText typography="caption">
                                Not available
                            </PrimaryText>
                        }
                    />

                    {props.checkboxTooltip ? (
                        <TooltipElement text={props.checkboxTooltip} />
                    ) : null}
                </div>
            ) : null}

            {props.displayErrorsBelowCheckbox && errors.memberId?.message ? (
                <InputErrorElement text={errors.memberId?.message || ""} />
            ) : null}
        </div>
    )
}

interface IDynamicFormMemberIdInputProps {
    className?: string
    label?: string
    isLabelStatic?: boolean
    formDefaultValue?: string
    withNotAvailableCheckbox: boolean
    notAvailableDefaultChecked?: boolean
    notAvailableDisabled?: boolean
    required?: boolean
    additionalValidations?: IIndexable
    disabled?: boolean
    formControlDisabled?: boolean
    hideErrors?: boolean
    forceHighlightOnError?: boolean
    displayErrorsBelowCheckbox?: boolean
    checkboxTooltip?: string
    onApplyPayerPrediction?: (payer: IPayer) => void
}
